// Vista previa interactiva de la encuesta (prueba la lógica condicional; no envía nada).
import type { JSX } from "react";
import { useState } from "react";
import { DEFAULT_CONSENT_TEXT } from "../forms/shared.tsx";
import type { Question, ShowIf, SurveySettings } from "./types.ts";

type Answer = string | string[] | number;

function visible(c: ShowIf | null | undefined, a: Record<string, Answer>): boolean {
  if (!c) return true;
  const v = a[c.q];
  const has = v !== undefined && v !== "" && !(Array.isArray(v) && !v.length);
  if (c.op === "answered") return has;
  if (!has) return c.op === "neq";
  const val = c.value ?? "";
  if (Array.isArray(v)) return c.op === "neq" ? !v.includes(val) : v.includes(val);
  return c.op === "neq" ? String(v) !== val : String(v) === val;
}

export function SurveyPreview({ questions, settings, mobile }: { questions: Question[]; settings: SurveySettings; mobile?: boolean }): JSX.Element {
  const [ans, setAns] = useState<Record<string, Answer>>({});
  const color = /^#[0-9a-fA-F]{3,8}$/.test(settings.color ?? "") ? settings.color! : "#21386d";
  const set = (id: string, v: Answer | undefined): void => setAns((p) => { const n = { ...p }; if (v === undefined || v === "" || (Array.isArray(v) && !v.length)) delete n[id]; else n[id] = v; return n; });
  // Visibilidad en orden: las respuestas de preguntas ocultas no cuentan.
  const eff: Record<string, Answer> = {};
  const shown = new Set<string>();
  for (const q of questions) if (visible(q.showIf, eff)) { shown.add(q.id); if (ans[q.id] !== undefined) eff[q.id] = ans[q.id]!; }
  const input = "w-full rounded-lg border border-gray-300 bg-white text-gray-900 px-3 py-2 text-[15px]";

  return (
    <div className="flex justify-center py-6 px-3 rounded-md bg-canvas">
      <div className="w-full bg-white text-gray-800 rounded-xl shadow-sm overflow-hidden" style={{ maxWidth: mobile ? 375 : 680, fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,Arial,sans-serif" }}>
        {(settings.intro?.title || settings.intro?.text) && (
          <div className="px-7 py-6 text-white" style={{ background: color }}>
            {settings.intro?.title && <h2 className="text-xl font-bold mb-1">{settings.intro.title}</h2>}
            {settings.intro?.text && <p className="opacity-90 whitespace-pre-line">{settings.intro.text}</p>}
          </div>
        )}
        <div className="px-7 pb-7 pt-2">
          {questions.map((q) => shown.has(q.id) && (
            <div key={q.id} className="py-4 border-b border-gray-100 last:border-0">
              <div className="font-semibold mb-1">{q.label}{q.required && <span className="text-red-600"> *</span>}</div>
              {q.description && <div className="text-sm text-gray-500 mb-2 whitespace-pre-line">{q.description}</div>}
              {(q.type === "short" || q.type === "name" || q.type === "email" || q.type === "phone") && <input className={input} placeholder={q.placeholder} value={String(ans[q.id] ?? "")} onChange={(e) => set(q.id, e.target.value)} />}
              {q.type === "long" && <textarea className={input} rows={3} placeholder={q.placeholder} value={String(ans[q.id] ?? "")} onChange={(e) => set(q.id, e.target.value)} />}
              {(q.type === "single" || q.type === "multiple") && (q.options ?? []).map((o) => {
                const cur = ans[q.id];
                const checked = q.type === "single" ? cur === o : Array.isArray(cur) && cur.includes(o);
                return (
                  <label key={o} className="flex gap-2 items-center border border-gray-200 rounded-lg px-3 py-2 mt-1.5 cursor-pointer">
                    <input type={q.type === "single" ? "radio" : "checkbox"} checked={checked} style={{ accentColor: color }}
                      onChange={(e) => {
                        if (q.type === "single") set(q.id, o);
                        else { const arr = Array.isArray(cur) ? [...cur] : []; set(q.id, e.target.checked ? [...arr, o] : arr.filter((x) => x !== o)); }
                      }} />{o}
                  </label>
                );
              })}
              {q.type === "dropdown" && <select className={input} value={String(ans[q.id] ?? "")} onChange={(e) => set(q.id, e.target.value)}><option value="">{q.placeholder || "Selecciona…"}</option>{(q.options ?? []).map((o) => <option key={o}>{o}</option>)}</select>}
              {(q.type === "scale5" || q.type === "scale10" || q.type === "nps" || q.type === "yesno") && (
                <>
                  <div className="flex flex-wrap gap-1.5">
                    {(q.type === "yesno" ? [["yes", "Sí"], ["no", "No"]] as Array<[string, string]>
                      : Array.from({ length: q.type === "scale5" ? 5 : q.type === "nps" ? 11 : 10 }, (_, i) => { const n = q.type === "nps" ? i : i + 1; return [n, String(n)] as [number, string]; })
                    ).map(([v, l]) => {
                      const on = ans[q.id] === v;
                      return <button key={l} type="button" onClick={() => set(q.id, on ? undefined : v)} className="flex-1 min-w-[34px] py-2 rounded-lg border font-semibold"
                        style={on ? { background: color, borderColor: color, color: "#fff" } : { borderColor: "#d1d5db", color: "#374151" }}>{l}</button>;
                    })}
                  </div>
                  {q.type === "nps" && <div className="flex justify-between text-xs text-gray-500 mt-1"><span>Nada probable</span><span>Muy probable</span></div>}
                </>
              )}
            </div>
          ))}
          {settings.consent?.enabled && <label className="flex gap-2 text-sm mt-4"><input type="checkbox" /> <span>{settings.consent.text || DEFAULT_CONSENT_TEXT} <u>Política de privacidad</u> <span className="text-red-600">*</span></span></label>}
          <button type="button" className="mt-5 w-full py-3 rounded-lg text-white font-semibold" style={{ background: color }}>{settings.buttonLabel || "Enviar respuestas"}</button>
          {Object.keys(ans).length > 0 && <button type="button" className="mt-2 text-xs text-gray-500 underline" onClick={() => setAns({})}>Reiniciar vista previa</button>}
        </div>
      </div>
    </div>
  );
}
